/**
 * Viewer Helper Functions
 * Utilities for creating ViewerData from various sources
 */

import type { ViewerData } from '../types/viewer';

/**
 * Create ViewerData for a PDF document.
 * Now uses view_url from backend when available, falls back to constructing URL if needed.
 * When view_url is used, we append ?token= so pdf.js (which cannot send Authorization header) can load the PDF in production.
 */
const _getViewUrl = (citation: any): string | undefined => {
  return citation.view_url || citation.document?.view_url;
};

/** Token for authenticated document requests (backend accepts ?token= for /documents/.../view). */
const _getAuthToken = (): string | null => {
  return localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
};

/** Append token to view URL so pdf.js GET can load the PDF (no Authorization header possible). */
const _appendTokenToViewUrl = (viewUrl: string, token: string | null): string => {
  if (!token) return viewUrl;
  const sep = viewUrl.includes('?') ? '&' : '?';
  return `${viewUrl}${sep}token=${encodeURIComponent(token)}`;
};

const _buildDocumentIdUrl = (
  documentId: string,
  apiBaseUrl: string,
  token: string | null
): string => {
  const basePath = `${apiBaseUrl}/documents/${documentId}/view`;
  return token ? `${basePath}?token=${token}` : basePath;
};

const _buildDocumentNameUrl = (
  documentName: string,
  apiBaseUrl: string,
  token: string | null
): string => {
  const normalizedFilename = documentName.replace(/ /g, '_');
  const encodedFilename = encodeURIComponent(normalizedFilename);
  const basePath = `${apiBaseUrl}/documents/view/${encodedFilename}`;
  return token ? `${basePath}?token=${token}` : basePath;
};

const _buildFallbackDocumentPath = (citation: any): string | undefined => {
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8001/api';
  const token = _getAuthToken();

  if (citation.document_id || citation.documentId) {
    return _buildDocumentIdUrl(citation.document_id || citation.documentId, apiBaseUrl, token);
  }

  if (citation.documentPath) {
    return citation.documentPath;
  }

  if (citation.document_name) {
    return _buildDocumentNameUrl(citation.document_name, apiBaseUrl, token);
  }

  return undefined;
};

const _getDocumentPath = (citation: any): string => {
  const viewUrl = _getViewUrl(citation);
  if (viewUrl) {
    return _appendTokenToViewUrl(viewUrl, _getAuthToken());
  }
  return _buildFallbackDocumentPath(citation) || '';
};

export const createPDFViewerData = (citation: any): ViewerData => {
  return {
    type: 'pdf',
    documentPath: _getDocumentPath(citation),
    documentName: citation.documentName || citation.document_name || '',
    documentDisplayName: citation.documentDisplayName || citation.document_display_name,
    documentId: citation.document_id || citation.documentId,
    page: citation.page || 1,
    highlightText: citation.highlightText || citation.text,
    chunkId: citation.chunk_id || citation.chunkId,
    snippetCoordinates: citation.snippet_coordinates || citation.snippetCoordinates,
    metadata: citation.metadata || citation,
  };
};

/**
 * Create ViewerData for a snippet
 */
export const createSnippetViewerData = (textContent: string, snippet: any): ViewerData => ({
  type: 'snippet',
  snippetId: snippet.id,
  documentId: snippet.document_id,
  documentName: snippet.document_name,
  title: snippet.title,
  textContent: textContent || snippet.text_content,
  pageNumber: snippet.page_number,
  coordinates: snippet.coordinates,
  imageData: snippet.image_data,
  metadata: snippet.metadata,
});

/**
 * Create ViewerData for a chunk
 */
export const createChunkViewerData = (chunk: any): ViewerData => ({
  type: 'chunk',
  chunkId: chunk.chunk_id || chunk.id,
  documentId: chunk.document_id,
  documentName: chunk.document_name,
  textContent: chunk.text || chunk.textContent,
  pageNumber: chunk.page_number,
  metadata: chunk.metadata,
});
